import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AddressMap from './AddressMap';
import StudioRulesPopup from '../popups/StudioRulesPopup';
import { useTheme } from '../../context/ThemeContext';
import './AboutUs.css';

// Array of about us images to cycle through
const ABOUT_IMAGES = [
  '/images/backgrounds/TiDeaSolis-instruktorky-11.jpg',
  '/images/backgrounds/TiDeaSolis-priestor-5.jpg',
  '/images/backgrounds/TiDeaSolis-instruktorky-12.jpg'
];

const AboutUs = () => {
  const { t } = useTranslation();
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showStudioRules, setShowStudioRules] = useState(false);
  
  // Add UTF-8 meta tag to ensure proper encoding
  useEffect(() => {
    // Check if the charset meta tag already exists
    let charsetMeta = document.querySelector('meta[charset]');
    if (!charsetMeta) {
      // Create and append the charset meta tag if it doesn't exist
      charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      document.head.insertBefore(charsetMeta, document.head.firstChild);
    } else {
      // Ensure it's set to UTF-8
      charsetMeta.setAttribute('charset', 'utf-8');
    }
    
    // Also ensure Content-Type meta is present
    let contentTypeMeta = document.querySelector('meta[http-equiv="Content-Type"]');
    if (!contentTypeMeta) {
      contentTypeMeta = document.createElement('meta');
      contentTypeMeta.setAttribute('http-equiv', 'Content-Type');
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
      document.head.appendChild(contentTypeMeta);
    } else {
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
    }
    
    // Add emoji and special character support with appropriate font
    let fontPreload = document.querySelector('link[rel="preload"][href*="noto-emoji"]');
    if (!fontPreload) {
      fontPreload = document.createElement('link');
      fontPreload.rel = 'preload';
      fontPreload.href = 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap';
      fontPreload.as = 'style';
      document.head.appendChild(fontPreload);
      
      // Also add the actual font style
      let fontStyle = document.createElement('link');
      fontStyle.rel = 'stylesheet';
      fontStyle.href = 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap';
      document.head.appendChild(fontStyle);
    }
  }, []);
  
  // Image carousel effect - switches every 15 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % ABOUT_IMAGES.length);
    }, 10000);
    
    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);
  
  // Preload and cache about images
  useEffect(() => {
    const cacheAboutImages = async () => {
      try {
        // Cache key for the about images in localStorage 
        const CACHE_KEY = 'yoga_about_images_cache';
        
        // Check if we have a valid cache entry with timestamp
        const cachedData = localStorage.getItem(CACHE_KEY);
        
        if (cachedData) {
          const { timestamp, imageUrls } = JSON.parse(cachedData);
          const currentTime = new Date().getTime();
          const twelveHoursMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
          
          // If the cache is less than 12 hours old, use it
          if (currentTime - timestamp < twelveHoursMs) {
            return;
          }
        }
        
        // If we don't have a valid cache, preload all images
        const preloadPromises = ABOUT_IMAGES.map(imageUrl => {
          return new Promise((resolve) => {
            const imageToCache = new Image();
            imageToCache.src = imageUrl;
            imageToCache.onload = () => resolve();
            
            // Also use link preload for better browser caching
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = imageUrl;
            link.as = 'image';
            document.head.appendChild(link);
          });
        });
        
        // Wait for all images to be preloaded
        await Promise.all(preloadPromises);
        
        // Store in localStorage with current timestamp
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: new Date().getTime(),
          imageUrls: ABOUT_IMAGES
        }));
        
      } catch (error) {
        // Silent fail for image caching
      }
    };
    
    cacheAboutImages();
  }, []);
  
  // Custom yoga type list items with proper encoding and styling
  // Sorted from longest to shortest name based on Slovak translations
  const yogaTypes = [
    "yoga_type_harmonia_balans",     // "Harmónia a balans yoga"
    "yoga_type_healthy_back", 
    "yoga_type_senior",      // "Senior yoga"
    // "yoga_type_hatha_physio",        // "Ranná Hatha yoga"
    "yoga_type_vinyasa_flow",        // "Vinyasa flow yoga"
    "yoga_type_pregnancy_postpartum", // "Gravid yoga"
    "yoga_type_Vedomá_yoga",         // "Vedomá yoga"
    "yoga_type_sila_v_rovnovahe",    // "Yoga pre zdravý chrbát"
    "yoga_type_power",               // "Power yoga"
    "yoga_type_facial",              // "Face yoga"
    "yoga_type_kids",                // "Kids yoga"
    "yoga_type_pilates"              // "Pilates"
  ];

  return (
    <section className="about-us-section" id="about-us">
      <div className="about-us-container">
        <div className="about-us-header">
          <h2 className="about-title" style={{ color: theme?.neutral2 || '#495057' }}>{t("about_us")}</h2>
          <div className="about-slogan">
            <p style={{ color: theme?.primary || '#78A6C8' }}>{t("breathe_discover_lightness")}</p>
          </div>
        </div>
        
        <div className="about-us-content">
          <div className="about-us-text">
            <div className="about-block who-we-are">
              <h3 style={{ color: theme?.primary || '#A67DB8' }}>{t("who_we_are")}</h3>
              <p>{t("welcome")}</p>
              <p dangerouslySetInnerHTML={{ __html: t("who_we_are_description") }} />
              <p className="learn-more-link">
                <Link to="/yoga-lore" className="yoga-lore-link" style={{ color: theme?.primary || '#A67DB8', borderBottomColor: `rgba(${theme?.primaryRGB || '166, 125, 184'}, 0.3)` }}>
                  {t("learn_about_yoga", "Learn more about yoga")} &raquo;
                </Link>
              </p>
            </div>
            
            <div className="about-block what-we-offer">
              <h3 style={{ color: theme?.primary || '#A67DB8' }}>{t("what_we_offer")}</h3>
              <p className="yoga-types-text">
                {yogaTypes.map((typeKey, index) => (
                  <React.Fragment key={index}>
                    - {t(typeKey)}
                    {index < yogaTypes.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
              <p dangerouslySetInnerHTML={{ __html: t("class_selection_note") }} />
              <p className="learn-more-link">
                <a href="#" className="yoga-lore-link" 
                   style={{ color: theme?.primary || '#A67DB8', borderBottomColor: `rgba(${theme?.primaryRGB || '166, 125, 184'}, 0.3)` }}
                   onClick={(e) => { e.preventDefault(); setShowStudioRules(true); }}>
                  {t("studio_rules", "Studio rules")} &raquo;
                </a>
              </p>
            </div>
            
            <div className="about-block additional-info">
              <p dangerouslySetInnerHTML={{ __html: t("additional_info") }} />
              <p>
                {t("contact_for_rental")}{" "}
                <a href="mailto:tideasolis@gmail.com" style={{ color: theme?.primary || '#78A6C8', borderBottomColor: theme?.primary || '#78A6C8' }}>tideasolis@gmail.com</a>.
              </p>
              <p dangerouslySetInnerHTML={{ __html: t("looking_forward") }} />
              <p className="team-signature" style={{ color: theme?.primary || '#78A6C8' }} dangerouslySetInnerHTML={{ __html: t("team_signature") }} />
            </div>
          </div>
          
          <div className="about-us-right-content">
            <div className="featured-image-container">
              <img 
                src={ABOUT_IMAGES[currentImageIndex]} 
                alt={t("yoga_studio_team_alt", "Yoga Studio Team and Space")}
                className="featured-image"
              />
            </div>
            
            <div className="about-us-map-container">
              <AddressMap />
            </div>
          </div>
        </div>
      </div>
      
      {showStudioRules && <StudioRulesPopup onClose={() => setShowStudioRules(false)} />}
    </section>
  );
};

export default AboutUs;